import { createReducer } from '@reduxjs/toolkit';
import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import truncate from 'truncate-html';
import moment from 'moment-timezone';
import { isObject, isArray } from 'lodash';
import config from '../config';

const isSSOLoggedIn = async () => {
  if (window.ssoClient) {
    return (await window.ssoClient.getAccessToken()) || false;
  }
  return false;
};

// Custom hook to check SSO status from COP Common Header
const useCOPSSOStatus = () => {
  const [ssoStatus, setSSOStatus] = useState(false);

  useEffect(() => {
    const waitForCHConfig = async () => {
      let retries = 0;
      const maxRetries = 50; // 10 seconds with 200ms intervals

      while (retries < maxRetries) {
        if (window.hktdcCH && typeof window.hktdcCH.getMenuProperties === 'function') {
          try {
            const chConfig = window.hktdcCH.getMenuProperties();
            if (chConfig && chConfig.sso) {
              console.log('COP SSO Config:', { chConfig });
              setSSOStatus(chConfig.sso.isAuthenticated || false);
              return;
            }
          } catch (error) {
            console.log('Error getting COP CH config:', error);
          }
        }

        await new Promise((resolve) => setTimeout(resolve, 200));
        retries++;
      }
      console.log('COP SSO status check timed out');
    };

    waitForCHConfig();
  }, []);

  return { ssoStatus };
};

const isExternalURL = (str) => {
  const regex = /(http|https):\/\/(\w+:{0,1}\w*)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%!\-\/]))?/;
  if (!regex.test(str)) {
    return false;
  }
  return true;
};

const constructPath = (routeTo, lang, pageType, pageName) => {
  return `${routeTo}/${lang}/${pageType}/${pageName}`;
};

const newEle = (id, src, target, tag = 'script', type = '', async = true) => {
  let js = document.getElementById(id);
  if (js) {
    js.parentNode.removeChild(js); // don't use .remove() when IE doesn't work
  }
  if (tag === 'stylesheet') {
    const jsPreloadId = `${id}PreLoad`;
    let jsPreload = document.getElementById(jsPreloadId);
    if (jsPreload) {
      jsPreload.parentNode.removeChild(jsPreload); // don't use .remove() when IE doesn't work
    }

    jsPreload = document.createElement('link');
    jsPreload.setAttribute('href', src);
    jsPreload.setAttribute('rel', 'preload');
    jsPreload.setAttribute('as', 'style');
    jsPreload.setAttribute('id', jsPreloadId);
    jsPreload.async = async;
    if (type !== '') jsPreload.setAttribute('type', type);
    target.appendChild(jsPreload);

    js = document.createElement('link');
    js.setAttribute('href', src);
    js.setAttribute('rel', 'stylesheet');
  } else {
    js = document.createElement(tag);
    js.setAttribute('src', src);
    js.setAttribute('charset', 'UTF-8');
  }
  js.async = async;
  js.setAttribute('id', id);
  if (type !== '') js.setAttribute('type', type);
  target.appendChild(js);
};

const is_Preview = () => {
  const stage = process.env.REACT_APP_STAGE || '';
  return stage.includes('preview');
};

const defaultReducerSchema = (
  types,
  initialState = {
    isLoading: false,
    data: [],
    error: false,
  },
) => {
  return createReducer(initialState, {
    [types.ACTION_RESET]: () => ({
      ...initialState,
    }),
    [types.ACTION_ERROR]: (state, action) => ({
      ...state,
      isLoading: false,
      data: action.payload,
      error: true,
    }),
    [types.ACTION_SUCCESS]: (state, action) => ({
      ...state,
      isLoading: false,
      data: action.payload,
      error: false,
    }),
    [types.ACTION_REQUEST]: (state, action) => ({
      ...state,
      isLoading: true,
      ...(action.payload && { params: action.payload }),
      error: false,
    }),
  });
};

const sanitizedHTML = (html) => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: config.allowedTags,
    ALLOWED_ATTR: config.allowedAttributes,
  });
};

const parseSanitizedHTML = (html, length) => {
  if (length) {
    return parse(sanitizedHTML(truncate(html, length)));
  }
  return parse(sanitizedHTML(html));
};

const currentDate = () => {
  const previewDate = new URLSearchParams(window.location.search).get('previewDate') || null;
  return previewDate ? moment(previewDate).toDate() : moment().toDate();
};

const momentInHK = (date) => {
  if (!date) return null;

  if (typeof date === 'string') {
    const hasTimeZone = /[Zz]|[+-]\d{2}:?\d{2}$/.test(date);
    const parsed = hasTimeZone ? moment(date) : moment.utc(date);
    if (parsed.isValid()) {
      return parsed.tz('Asia/Hong_Kong');
    }
  }

  const parsed = moment(date);
  if (!parsed.isValid()) return null;
  return parsed.tz('Asia/Hong_Kong');
};

// Parsing Date from CMS, most likely using UTC Datetime such as 2018-01-01 12:00:00
const momentUTCDate = (dateString) => {
  return moment.tz(dateString, 'UTC').toDate();
};

const isDateBetweenDateRange = (minDateString, maxDateString, currentDateString) => {
  const compareDate = moment(currentDateString).toDate();
  const minDate = momentUTCDate(minDateString);
  const maxDate = momentUTCDate(maxDateString);
  return compareDate >= minDate && compareDate <= maxDate;
};

const isIsoDate = (str) => {
  if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)) return false;
  var d = new Date(str);
  return d.toISOString() === str;
};

const convertToISODate = (str) => {
  if (isIsoDate(str)) return str;
  else return momentUTCDate(str);
};

const forceToArray = (data) => {
  if (isArray(data)) {
    return data;
  }
  if (isObject(data) && !Object.values(data).every((x) => x === null || x === '')) {
    return [data];
  }
  return null;
};

const localeList = (languages) => {
  return languages
    .split(',')
    .map((v) => v.trim())
    .join('|');
};

const fireTealiumEvent = (tealiumData) => {
  const fireTealiumAction = () => {
    window.utag.link(JSON.parse(tealiumData));
  };

  const fireTealium = () => {
    if (window.utag) {
      fireTealiumAction();
    } else {
      console.log('====================================');
      console.log('window utag not ready');
      console.log('====================================');

      let tryCount = 0;
      const waitTealiumLibraryInit = setInterval(() => {
        console.log('waitTealiumLibraryInit.....');
        tryCount++;
        if (window.utag) {
          fireTealiumAction();
          tryCount = 0;
          clearInterval(waitTealiumLibraryInit);
        }
        if (tryCount > config.interval.times) {
          console.log(`TealiumLibrary init Fail within ${config.interval.times} try`);
          tryCount = 0;
          clearInterval(waitTealiumLibraryInit);
        }
      }, config.interval.frequency);
    }
  };

  fireTealium();
};

const shortFormatDate = ({ date, locale }) => {
  if (!date) return '';
  const dateObj = momentInHK(date);
  if (!dateObj) return '';

  const day = dateObj.date();
  const year = dateObj.year();

  if (locale === 'en') {
    const month = dateObj.format('MMM');
    return `${day} ${month} ${year}`;
  } else {
    const month = dateObj.format('M');
    return `${year}年${month}月${day}日`;
  }
};

const getDatePeriodFormatted = ({ startDate, endDate, locale, t }) => {
  if (!startDate || !endDate) return '';

  const startMoment = momentInHK(startDate);
  const endMoment = momentInHK(endDate);
  if (!startMoment || !endMoment) return '';

  // Check different date scenarios
  const isSameDate = startMoment.isSame(endMoment, 'day');
  const isSameMonth = startMoment.isSame(endMoment, 'month');
  const isSameYear = startMoment.isSame(endMoment, 'year');

  // Get separator based on locale
  const getSeparator = () => {
    if (locale === 'en') return ' - ';
    return '至'; // You can customize this for different locales if needed
  };

  const separator = getSeparator();

  if (isSameDate) {
    // Same date: return single date
    return shortFormatDate({ date: startDate, locale });
  } else if (isSameMonth) {
    // Same month: show "22 - 25 Jan 2026" or "1月22日 - 25日"
    if (locale === 'en') {
      const startDay = startMoment.format('D');
      const endFormatted = shortFormatDate({ date: endDate, locale });
      return `${startDay}${separator}${endFormatted}`;
    } else {
      // For Chinese: "1月22日 - 25日"
      const year = startMoment.format('YYYY');
      const month = startMoment.format('M');
      const startDay = startMoment.format('D');
      const endDay = endMoment.format('D');
      return `${year}年${month}月${startDay}日${separator}${endDay}日`;
    }
  } else if (isSameYear) {
    // Same year: show "22 Jan - 25 Feb 2026" or "2026年1月22日 - 2月25日"
    if (locale === 'en') {
      const startFormatted = startMoment.format('D MMM');
      const endFormatted = shortFormatDate({ date: endDate, locale });
      return `${startFormatted}${separator}${endFormatted}`;
    } else {
      // For Chinese: "2026年1月22日 - 2月25日"
      const year = startMoment.format('YYYY');
      const startMonth = startMoment.format('M');
      const startDay = startMoment.format('D');
      const endMonth = endMoment.format('M');
      const endDay = endMoment.format('D');
      return `${year}年${startMonth}月${startDay}日${separator}${endMonth}月${endDay}日`;
    }
  } else {
    // Different years: show full dates
    const formattedStartDate = shortFormatDate({ date: startDate, locale });
    const formattedEndDate = shortFormatDate({ date: endDate, locale });
    return `${formattedStartDate}${separator}${formattedEndDate}`;
  }
};

const sectionStyle = (image) =>
  image
    ? {
        backgroundImage: `url(${image})`,
      }
    : {};

const convertImageUrlWithBase64 = ({ url, needResize = true, width = 675, height = 450 }) => {
  if (!url) return '';
  try {
    const base64 = url.split('/').slice(-1)[0];
    const decoded = JSON.parse(atob(base64));
    /* console.log({ decoded }); */
    decoded.format = 'webp';
    if (needResize) {
      decoded.edits = { resize: { width: width, height: height, fit: 'inside' } };
    }
    decoded.quality = 100;
    const newBase64 = btoa(JSON.stringify(decoded));
    const origin = new URL(url).origin;
    return `${origin}/${newBase64}`;
  } catch {
    return url;
  }
};

const scrollToContactUs = () => {
  const scroll = () => {
    const target = document.getElementById('contact-us');
    if (!target) return;

    const isMobile = window.matchMedia('(max-width: 992px)').matches;
    const headerOffset = isMobile ? 0 : 80;
    const targetPosition = target.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({
      top: Math.max(0, targetPosition - headerOffset),
      behavior: isMobile ? 'auto' : 'smooth',
    });
  };

  [0, 300, 1000].forEach((delay) => window.setTimeout(scroll, delay));
};

const toPlainText = (value = '') =>
  String(value == null ? '' : value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export {
  isExternalURL,
  constructPath,
  newEle,
  is_Preview,
  defaultReducerSchema,
  sanitizedHTML,
  parseSanitizedHTML,
  currentDate,
  momentInHK,
  momentUTCDate,
  isDateBetweenDateRange,
  forceToArray,
  localeList,
  fireTealiumEvent,
  isSSOLoggedIn,
  useCOPSSOStatus,
  shortFormatDate,
  getDatePeriodFormatted,
  convertToISODate,
  sectionStyle,
  convertImageUrlWithBase64,
  scrollToContactUs,
  toPlainText,
};
